import styles from './hoardStatusPDFPage.module.css'
import { Customer, HoardOrder } from "../../../state/types"
import { Fragment, useEffect, useMemo, useState } from 'react'
import { DocumentKey, Employee, useEmployeeLoaders, usePaymentTypeLoaders } from '../../../../administration'
import { useProductHooks, useProductLoaders } from '../../../../products'
import { useDataTypesHooks } from '../../../../../features/data-types'
import { useBranchLoaders } from '../../../../manager'
import { usePriceOutlineHooks } from '../../../../operations'
import { PDFHeader } from '../../../../../features/ui'
import { HoardStatusItem, HoardStatusPDFRow, HoardStatusSection } from './types'
import { useCustomerLoaders } from '../../../loaders/customerLoaders'
import { useCustomerTypeLoaders } from '../../../loaders/customerTypeLoaders'
import { firstItem } from '../../../../../utils/listUtils'
import { addressHelpers } from '../../../../../features/address'
import { useHoardHooks} from '../../../hooks/hoardHooks'
import { useSalesHooks } from '../../../hooks/salesHooks'
import { useSaleOperationHooks } from '../../../hooks/saleOperationHooks'

type Props = {
  pageIndex: number
  hoard: HoardOrder
  items: HoardStatusItem[]
  pagesAmount: number
}

export const HoardStatusPDFPage = (props: Props) => {
  const { pageIndex, hoard, items, pagesAmount } = props
  const isLastPage = pageIndex === pagesAmount - 1

  const [customer, setCustomer] = useState<Customer>()
  const [seller, setSeller] = useState<Employee>()
  const [rows, setRows] = useState<HoardStatusPDFRow[]>([])
  const [downloadDate, setDownloadDate] = useState<Date>(new Date())

  const { describeAddress } = addressHelpers
  const { getItemPriceOutline } = usePriceOutlineHooks()
  const { formatDate, formatDateTime, formatPrice, formatIdentifier, formatPhone } = useDataTypesHooks()
  const { describeQuantity } = useProductHooks()
  const { getDocumentFooter } = useSalesHooks()
  const { describePaymentItemName } = useSaleOperationHooks()

  const branchLoaders = useBranchLoaders()
  const productLoaders = useProductLoaders()
  const customerLoaders = useCustomerLoaders()
  const customerTypeLoaders = useCustomerTypeLoaders()
  const { findCustomerType } = customerTypeLoaders()
  const employeeLoaders = useEmployeeLoaders()
  const paymentTypeLoaders = usePaymentTypeLoaders()

  const { getUsedCredit, getPendingCredit } = useHoardHooks()

  const branch = useMemo(() => branchLoaders().findBranch(hoard.branchId)!, [hoard])
  const documentFooter = useMemo(() => getDocumentFooter(DocumentKey.HOARD_STATUS), [getDocumentFooter])

  const columns = [
    { name: 'product', label: 'Producto' },
    { name: 'unitTotal', label: 'P. Unitario' },
    { name: 'quantity', label: 'Cantidad' },
    { name: 'total', label: 'P. Total' },
  ]

  const sectionTitles = new Map<HoardStatusSection, string>()
    .set(HoardStatusSection.DELIVERED, 'Materiales Entregados')
    .set(HoardStatusSection.PENDING, 'Pendientes de Entrega')
    .set(HoardStatusSection.RETURNED, 'Materiales Devueltos')

  const loadRows = async (): Promise<HoardStatusPDFRow[]> => {
    const rows: HoardStatusPDFRow[] = []

    for (const item of items) {
      const product = await productLoaders().loadProduct(item.productId)
      if (!product) continue
      const { specialDiscountUnitSubtotal } = getItemPriceOutline(item)
      // Returned items are subtracted from the used credit
      const sign = item.section === HoardStatusSection.RETURNED ? -1 : 1
      rows.push({
        product: product.name,
        unitTotal: formatPrice(specialDiscountUnitSubtotal),
        quantity: describeQuantity(item.quantity, product),
        total: formatPrice(sign * specialDiscountUnitSubtotal * item.quantity),
        section: item.section
      })
    }

    return rows
  }

  useEffect(() => {
    employeeLoaders().loadEmployee(hoard.sellerId).then(setSeller)
    customerLoaders().loadCustomer(hoard.customerId).then(setCustomer)
    loadRows().then(newRows => setRows(newRows))
    setDownloadDate(new Date())
  }, [hoard, items])

  return (
    <div className={styles.container}>
      <div className={styles.body}>
        <PDFHeader />

        {pageIndex === 0 && (
          <>
            <div className={styles.row}>
              <span className={styles.statusTitle}>
                Estado de Acopio #{hoard.code}
              </span>
              <div className={styles.date}>
                Fecha: {formatDateTime(downloadDate)}
              </div>
            </div>

            <div className={`${styles.row} ${styles.alignStart}`}>
              <div className={styles.infoSection}>
                <span>Cliente: {customer?.name}</span>
                <span>Dirección: {customer?.mainAddress?.address || '-'}</span>
                <span>DNI/CUIT: {customer ? formatIdentifier(customer.identifier) : '-'}</span>
                <span>
                  Teléfono:{' '}
                  {customer && customer.phones.length > 0
                    ? formatPhone(firstItem(customer!.phones))
                    : '-'}
                </span>
              </div>
              <div className={`${styles.infoSection} ${styles.rightSection}`}>
                <span>Vendedor: {seller?.name}</span>
                <span>
                  Teléfono: {seller?.phone ? formatPhone(seller.phone) : '-'}
                </span>
              </div>
            </div>

            <div className={`${styles.row} ${styles.alignStart}`}>
              <div className={styles.infoSection}>
                <span>Lista de Precios: {findCustomerType(hoard.customerTypeId)!.name}</span>
                <span>
                  {`Medio${hoard.paymentItems.length > 1 ? 's' : ''} de Pago: `}
                  {hoard.paymentItems.length > 0
                    ? hoard.paymentItems
                        .map(paymentItem => {
                          const amount =
                            hoard.paymentItems.length > 1
                              ? ` (${formatPrice(paymentItem.amount)})`
                              : ''
                          return `${describePaymentItemName(paymentItem)}${amount}`
                        })
                        .join(', ')
                    : paymentTypeLoaders().findPaymentType(hoard.paymentTypeId)?.name || 'Sin medio de pago'}
                </span>
                <span>Valor Acopiado: {formatPrice(hoard.value)}</span>
              </div>
              <div className={`${styles.infoSection} ${styles.rightSection}`}>
                <span>
                  Fecha Límite de Retiro: {formatDate(new Date(hoard.dueDate))}
                </span>
              </div>
             </div>
          </>
        )}

        {rows.length > 0 && (
          <>
            <div className={styles.tableRow}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    {columns.map((column, index) => (
                      <th key={index}>{column.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <Fragment key={index}>
                      {row.section !== rows[index - 1]?.section && (
                        <tr>
                          <td colSpan={columns.length} className={styles.sectionHeader}>
                            {sectionTitles.get(row.section)}
                          </td>
                        </tr>
                      )}
                      <tr>
                        {columns.map((column, idx) => (
                          <td key={idx}>
                            {row[column.name as keyof HoardStatusPDFRow]}
                          </td>
                        ))}
                      </tr>
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.pagination}>
              {pageIndex + 1} / {pagesAmount}
            </div>
          </>
        )}

        {isLastPage && (
          <div className={styles.summary}>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Valor Acopiado</span>
            </div>

            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel} style={{ fontWeight: 'bold' }}>Total: </span>
              <span className={styles.summaryValue} style={{ fontWeight: 'bold' }}>{formatPrice(hoard.value!)}</span>
            </div>

            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel} style={{ fontWeight: 'bold' }}>Utilizado: </span>
              <span className={styles.summaryValue} style={{ fontWeight: 'bold' }}>
                {formatPrice(getUsedCredit(hoard.items))}
              </span>
            </div>

            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel} style={{ fontWeight: 'bold' }}>Restante: </span>
              <span className={styles.summaryValue} style={{ fontWeight: 'bold' }}>
                {formatPrice(getPendingCredit(hoard))}
              </span>
            </div>
          </div>
        )}

        <div className={styles.footer}>
          <div className={styles.separator} />
          <div className={`${styles.row} ${styles.footerRow}`}>
            <span>
              Sucursal {branch.name}: {describeAddress(branch.address)}
              {branch.email ? ` - Mail: ${branch.email}` : ''}
            </span>
            <span>
              {documentFooter}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

